import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { queryKeys } from 'src/easyclaim.app.libs/queryKeys';
import { isAbortError, reportError } from 'src/easyclaim.app.libs/reportError';
import { reportWarning } from 'src/easyclaim.app.libs/reportWarning';
import {
	fetchThumbnail,
	ThumbnailNotReadyError,
} from 'src/easyclaim.app.services/ThumbnailsService';
import {
	useHasThumbnailCacheHydrated,
	usePhotoThumbnailStore,
	useThumbnailFromCache,
} from 'src/easyclaim.app.stores/usePhotoThumbnailStore';

import { useIsPerformanceEnhancerEnabled } from './useIsPerformanceEnhancerEnabled';

const MAX_POLL_ATTEMPTS = 2;
const MIN_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 8000;
const DEFAULT_THUMBNAIL_WIDTH = 400;
const DEFAULT_THUMBNAIL_HEIGHT = 300;

interface UseThumbnailOptions {
	caseId: string | undefined;
	imageId: string | undefined;
	width?: number;
	height?: number;
	enabled?: boolean;
}

export function useThumbnail({
	caseId,
	imageId,
	width = DEFAULT_THUMBNAIL_WIDTH,
	height = DEFAULT_THUMBNAIL_HEIGHT,
	enabled = true,
}: UseThumbnailOptions) {
	const cachedThumbnail = useThumbnailFromCache(imageId);
	const hasThumbnailCacheHydrated = useHasThumbnailCacheHydrated();
	const setThumbnail = usePhotoThumbnailStore((state) => state.setThumbnail);
	const isEnabled = useIsPerformanceEnhancerEnabled();

	const query = useQuery({
		queryKey: queryKeys.thumbnails.detail(caseId ?? '', imageId ?? '', width, height),
		queryFn: ({ signal }) =>
			fetchThumbnail({ caseId: caseId!, imageId: imageId!, width, height, signal }),
		enabled:
			isEnabled &&
			enabled &&
			hasThumbnailCacheHydrated &&
			!!caseId &&
			!!imageId &&
			!cachedThumbnail,
		staleTime: Infinity,
		retry: (failureCount, error) =>
			error instanceof ThumbnailNotReadyError && failureCount < MAX_POLL_ATTEMPTS,
		retryDelay: (attempt) => Math.min(MIN_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS),
	});

	useEffect(() => {
		if (query.data && imageId) {
			setThumbnail(imageId, query.data.blob);
			URL.revokeObjectURL(query.data.objectUrl);
		}
	}, [query.data, imageId, setThumbnail]);

	useEffect(() => {
		if (!isEnabled || !query.isError) {
			return;
		}

		if (isAbortError(query.error)) {
			return;
		}

		const extra = { caseId, imageId, width, height };

		if (query.error instanceof ThumbnailNotReadyError) {
			reportWarning('Thumbnail was not ready after polling', {
				...extra,
				pollAttempts: MAX_POLL_ATTEMPTS,
			});
		} else {
			reportError(query.error, { context: 'useThumbnail', ...extra });
		}
	}, [isEnabled, query.isError, query.error, caseId, imageId, width, height]);

	const shouldFallbackToLegacyImage =
		isEnabled && query.isError && query.error instanceof ThumbnailNotReadyError;

	return {
		...query,
		isEnabled,
		shouldFallbackToLegacyImage,
		thumbnail: isEnabled ? (cachedThumbnail ?? query.data?.objectUrl) : undefined,
	};
}
